import { useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import type { ConnectedRepo } from '@/types'

export function useRepos() {
  const { repos, setRepos, session } = useAppStore()

  const fetchRepos = useCallback(async () => {
    if (!session?.user) return

    const { data, error } = await supabase
      .from('connected_repos')
      .select('*')
      .order('last_accessed_at', { ascending: false, nullsFirst: false })
      .order('added_at', { ascending: false })

    if (error) {
      console.error('[ExMarked] Failed to fetch repos:', error.message)
      return
    }

    setRepos((data ?? []) as ConnectedRepo[])
  }, [session?.user, setRepos])

  useEffect(() => {
    void fetchRepos()
  }, [fetchRepos])

  const addRepo = async (
    owner: string,
    name: string,
    opts?: {
      description?:    string
      default_branch?: string
      is_private?:     boolean
      display_name?:   string
      avatar_url?:     string
    }
  ): Promise<ConnectedRepo> => {
    if (!session?.user) throw new Error('Not authenticated')

    const { data, error } = await supabase
      .from('connected_repos')
      .insert({
        user_id:        session.user.id,
        owner:          owner.trim(),
        name:           name.trim(),
        description:    opts?.description    ?? null,
        default_branch: opts?.default_branch ?? 'main',
        is_private:     opts?.is_private     ?? false,
        display_name:   opts?.display_name   ?? null,
        avatar_url:     opts?.avatar_url     ?? null,
      })
      .select()
      .single()

    if (error) throw error
    await fetchRepos()
    return data as ConnectedRepo
  }

  const removeRepo = async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('connected_repos')
      .delete()
      .eq('id', id)

    if (error) throw error
    setRepos(repos.filter((r) => r.id !== id))
  }

  const updateLastAccessed = async (id: string): Promise<void> => {
    await supabase
      .from('connected_repos')
      .update({ last_accessed_at: new Date().toISOString() })
      .eq('id', id)
  }

  return { repos, fetchRepos, addRepo, removeRepo, updateLastAccessed }
}
